/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useEffect } from 'react';
import { Button } from 'reactstrap';
import { Loader } from '@g17eco/atoms/loader';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { AnalyticsEvents } from '@services/analytics/AnalyticsEvents';
import { getAnalytics } from '@services/analytics/AnalyticsService';
import { getSDGOptions } from '@features/sdg';
import { InitiativeData, InitiativePlain, Materiality, MaterialityMap } from '@g17eco/types/initiative';
import { useUpdateMaterialityMutation } from '@api/initiatives';
import './style.scss';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';

type MaterialityGoals = {
  high: string[];
  medium: string[];
  low: string[];
};

interface SDGSelectorProps {
  initiative: InitiativePlain & Pick<InitiativeData, 'industry' | 'industryText'>;
  toggle: () => void;
  handleReload?: () => void;
  callbackSubmit?: () => void;
}

const SDGSelector = (props: SDGSelectorProps) => {
  const { handleReload, initiative, toggle, callbackSubmit = () => {} } = props;

  const [materialityGoals, setMaterialityGoals] = React.useState<MaterialityGoals>({
    high: [],
    medium: [],
    low: [],
  });

  useEffect(() => {
    if (initiative.materialityMap) {
      setMaterialityGoals({
        high: initiative.materialityMap.high ?? [],
        medium: initiative.materialityMap.medium ?? [],
        low: initiative.materialityMap.low ?? [],
      });
    }
  }, [initiative]);

  const [updateMateriality, { isLoading: isSaving }] = useUpdateMaterialityMutation();

  const sdgOptions = getSDGOptions();
  const sdgGoals = sdgOptions.filter((option) => option.value !== null);

  const getDisabledGoalByMateriality = (type: Materiality) => {
    switch (type) {
      case Materiality.High:
        return [...materialityGoals.low, ...materialityGoals.medium];
      case Materiality.Medium:
        return [...materialityGoals.high, ...materialityGoals.low];
      case Materiality.Low:
        return [...materialityGoals.high, ...materialityGoals.medium];
      case Materiality.None:
      default:
        return [...materialityGoals.high, ...materialityGoals.low, ...materialityGoals.medium];
    }
  };

  const getOptions = (type: Materiality) => {
    const disabledGoals = getDisabledGoalByMateriality(type);
    return sdgGoals.map((option) => ({
      ...option,
      isDisabled: disabledGoals.includes(option.value),
    }));
  };

  const [errorMessage, setMessage] = React.useState('');

  const onUpdate = () => {
    const none = sdgGoals.reduce((acc, option) => {
      if (
        !materialityGoals.high.includes(option.value) &&
        !materialityGoals.medium.includes(option.value) &&
        !materialityGoals.low.includes(option.value)
      ) {
        acc.push(option.value);
      }

      return acc;
    }, [] as string[]);

    handleUpdate({
      ...materialityGoals,
      none,
    });
    callbackSubmit();
  };

  const handleUpdate = async (materialityMap?: MaterialityMap) => {
    return updateMateriality({ initiativeId: initiative._id, materiality: materialityMap })
      .unwrap()
      .then(async () => {
        const analytics = getAnalytics();
        await analytics.track(AnalyticsEvents.MaterialityCustomized, {
          initiativeId: initiative._id,
        });
        if (handleReload) {
          handleReload();
        }
      })
      .catch((e: Error) => setMessage(e.message));
  };

  const selectProps = {
    isClearable: true,
    className: 'mt-2 text-nowrap',
    placeholder: 'Select SDGs',
    hideCheckboxOnDisabled: false,
  };

  return (
    <>
      {isSaving && <Loader />}
      {errorMessage && <div className='alert alert-primary'>{errorMessage}</div>}
      <p>Select the SDGs that are most important to you.</p>
      <div className='h5 mt-4'>
        <i className='fa fa-tachometer-alt text-danger mr-2' />
        High
      </div>
      <SelectFactory
        {...selectProps}
        selectType={SelectTypes.MultipleSelect}
        options={getOptions(Materiality.High)}
        onChange={(values) => setMaterialityGoals((prev) => ({ ...prev, high: values }))}
        values={materialityGoals.high}
      />
      <div className='h5 mt-4'>
        <i className='fa fa-tachometer-alt text-warning mr-2' />
        Medium
      </div>

      <SelectFactory
        {...selectProps}
        selectType={SelectTypes.MultipleSelect}
        options={getOptions(Materiality.Medium)}
        onChange={(values) => setMaterialityGoals((prev) => ({ ...prev, medium: values }))}
        values={materialityGoals.medium}
      />
      <div className='h5 mt-4'>
        <i className='fa fa-tachometer-alt text-success mr-2' />
        Low
      </div>
      <SelectFactory
        {...selectProps}
        selectType={SelectTypes.MultipleSelect}
        options={getOptions(Materiality.Low)}
        onChange={(values) => setMaterialityGoals((prev) => ({ ...prev, low: values }))}
        values={materialityGoals.low}
      />
      <div className='text-right mt-5 d-flex'>
        {/* This button is only shown if the initiative has industry which is on CT */}
        {initiative.industry ? (
          <SimpleTooltip
            text={
              !initiative.industryText
                ? 'Please select your sector, then click \'save\' to enable sector defaults'
                : undefined
            }
          >
            <Button
              color='link-secondary'
              onClick={() => handleUpdate()}
              disabled={isSaving || !initiative.industryText}
            >
              Use sector default
            </Button>
          </SimpleTooltip>
        ) : null}
        <Button color='link-secondary' onClick={toggle} className='ms-auto me-3'>
          Cancel
        </Button>
        <Button color='primary' disabled={errorMessage !== ''} onClick={() => onUpdate()}>
          Save
        </Button>
      </div>
    </>
  );
};

export default SDGSelector;
